import React, { memo, useEffect } from 'react'
import styled from 'styled-components'
import { Box, BoxProps, getPaletteColor } from 'pcln-design-system'
import Arrow from '../Arrow'
import useCalendar from '../useCalendar'
import useScroll from '../useScroll'
import { ICalendarCardProps } from '../types'

type CardProps = BoxProps & { hasBoxShadow?: boolean }

const Card = styled<React.FC<CardProps>>(Box)`
  position: relative;
  height: 100%;
  max-height: 100vh;
  ${(props) => props.theme.mediaQueries[1]} {
    box-shadow: ${(props) => (props.hasBoxShadow ? props.theme.shadows['overlay-lg'] : 'none')};
    height: auto;
  }
  background-color: ${getPaletteColor('background.lightest')};
`

const ScrollContainer = styled(Box)`
  overflow-y: scroll;

  & > div {
    scroll-behavior: ${(props: ICalendarCardProps) => (props.animateScroll ? 'smooth' : 'auto')};
    height: 100%;
    max-height: 100vh;
    overflow-y: scroll;
    -webkit-overflow-scrolling: touch;
    ${(props) => props.theme.mediaQueries[1]} {
      overflow: auto;
    }
  }
`

/** @public */
function CalendarCard({
  animateScroll,
  arrows = true,
  boxShadow = true,
  calendarWrapperStyles = {},
  children,
  monthsToShow = 2,
  monthWidth = 350,
  roundedCorners,
  scrollToMonth = 0,
  keyNavHandler,
  setCalendarRef,
}: ICalendarCardProps) {
  const { handleKeyDismiss } = useCalendar()
  const {
    monthIndex,
    scrollBoxDOMNode,
    scrollContainerDOMNode,
    showLeftArrow,
    showRightArrow,
    onArrowFocus,
    scrollLeft,
    scrollRight,
  } = useScroll()

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDismiss)

    return () => {
      document.removeEventListener('keydown', handleKeyDismiss)
    }
  }, [])

  useEffect(() => {
    const monthNode = scrollBoxDOMNode?.current?.children[0]?.children?.[scrollToMonth] as HTMLDivElement

    if (monthNode && scrollContainerDOMNode?.current) {
      scrollContainerDOMNode.current.scrollLeft = monthNode?.offsetLeft ?? 0
      scrollContainerDOMNode.current.scrollTop = monthNode?.offsetTop ?? 0
    }
  }, [])

  const childrenLength = React.Children.count(children) || 1

  return (
    <div ref={setCalendarRef}>
      <Card
        borderRadius={roundedCorners ? '2xl' : undefined}
        hasBoxShadow={boxShadow}
        rounded={roundedCorners ? 'top' : undefined}
        width={[1, 1, monthsToShow * monthWidth]}
      >
        {arrows && showLeftArrow && (
          <Arrow isPrev monthCount={monthIndex} roundedCorners={roundedCorners} onClick={scrollLeft} />
        )}
        <ScrollContainer
          animateScroll={animateScroll}
          borderRadius={roundedCorners ? '2xl' : undefined}
          data-testid='scroll-container'
          onKeyDown={keyNavHandler}
        >
          <div ref={scrollContainerDOMNode}>
            <div ref={scrollBoxDOMNode}>
              <Box {...calendarWrapperStyles} width={[1, 1, monthWidth * childrenLength]}>
                {children}
              </Box>
            </div>
          </div>
        </ScrollContainer>
        {arrows && showRightArrow && (
          <Arrow
            monthCount={monthIndex}
            monthsToShow={monthsToShow}
            roundedCorners={roundedCorners}
            onClick={scrollRight}
            onFocus={onArrowFocus}
          />
        )}
      </Card>
    </div>
  )
}

export default memo(CalendarCard)
